// The "open the book" modal. Two screens:
//
//   1. Mode shelf — four cards: Starter → Explorer → Inventor → Genius.
//   2. Course list — clicking a mode reveals the 2 courses bundled for
//      that mode. Clicking a course closes the modal and hands the
//      Course off to `onPickCourse` (App wires this to the course
//      runner's `start`).
//
// Visual conceit: the panel is laid out like an open book (two
// pages side-by-side). On narrow viewports we collapse to a single
// column. CSS lives in `apps/web/src/styles/index.css` under the
// `.courses-modal` selector group.

import { useEffect, useState } from "react";

import { coursesForMode, type CourseMode, MODES, MODE_META, type Course } from "./types-and-data";

export interface CoursesModalProps {
  open: boolean;
  onClose: () => void;
  onPickCourse: (course: Course) => void;
  /** User-generated (Text-to-Course) courses, merged in alongside the
   * bundled ones per mode. */
  extraCourses?: Course[];
}

/** Bundled + user-generated courses for a mode, user ones last. */
function coursesForModeWith(mode: CourseMode, extra: Course[]): Course[] {
  return [...coursesForMode(mode), ...extra.filter((c) => c.mode === mode)];
}

export function CoursesModal({
  open,
  onClose,
  onPickCourse,
  extraCourses = [],
}: CoursesModalProps) {
  // Which mode's page is showing. `null` = mode shelf (the "table of
  // contents" view); a mode value = the course list for that mode.
  const [openedMode, setOpenedMode] = useState<CourseMode | null>(null);

  // Reset to the shelf every time the modal opens so the user
  // always lands on the chapter list.
  useEffect(() => {
    if (open) setOpenedMode(null);
  }, [open]);

  // Esc closes the modal — same affordance as the existing AuthModal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="courses-modal-backdrop"
      onClick={(e) => {
        // Only close when the click is on the backdrop itself, not on
        // a child (so clicking inside the book doesn't dismiss it).
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="courses-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Courses"
      >
        <header className="courses-modal-header">
          <h2 className="courses-modal-title">
            {openedMode === null
              ? "Courses"
              : `${MODE_META[openedMode].title} courses`}
          </h2>
          <div className="courses-modal-header-actions">
            {openedMode !== null && (
              <button
                type="button"
                className="courses-link"
                onClick={() => setOpenedMode(null)}
                aria-label="Back to chapter list"
              >
                ← Chapters
              </button>
            )}
            <button
              type="button"
              className="courses-close"
              onClick={onClose}
              aria-label="Close"
              title="Close"
            >
              ×
            </button>
          </div>
        </header>

        {openedMode === null ? (
          <ModeShelf onPick={setOpenedMode} extraCourses={extraCourses} />
        ) : (
          <CourseList
            mode={openedMode}
            extraCourses={extraCourses}
            onPick={(c) => {
              onPickCourse(c);
              onClose();
            }}
          />
        )}
      </div>
    </div>
  );
}

function ModeShelf({
  onPick,
  extraCourses,
}: {
  onPick: (m: CourseMode) => void;
  extraCourses: Course[];
}) {
  return (
    <ol className="courses-mode-shelf" role="list">
      {MODES.map((m) => {
        const meta = MODE_META[m];
        const count = coursesForModeWith(m, extraCourses).length;
        return (
          <li key={m} className={`courses-mode-card mode-${m}`}>
            <button
              type="button"
              className="courses-mode-button"
              onClick={() => onPick(m)}
            >
              <span className="courses-mode-badge" aria-hidden="true">
                {meta.badge}
              </span>
              <span className="courses-mode-text">
                <span className="courses-mode-title">{meta.title}</span>
                <span className="courses-mode-tagline">{meta.tagline}</span>
                <span className="courses-mode-count">
                  {count} {count === 1 ? "course" : "courses"}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function CourseList({
  mode,
  onPick,
  extraCourses,
}: {
  mode: CourseMode;
  onPick: (course: Course) => void;
  extraCourses: Course[];
}) {
  const list = coursesForModeWith(mode, extraCourses);
  return (
    <ol className="courses-list" role="list">
      {list.map((c) => (
        <li key={c.id} className="courses-list-item">
          <button
            type="button"
            className="courses-list-button"
            onClick={() => onPick(c)}
          >
            <span className="courses-list-title">{c.title}</span>
            <span className="courses-list-summary">{c.summary}</span>
            <span className="courses-list-meta">
              {c.steps.length} steps · {countNarrations(c)} explanations
            </span>
          </button>
        </li>
      ))}
    </ol>
  );
}

function countNarrations(c: Course): number {
  return c.steps.filter((s) => s.kind === "narrate").length;
}
